import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { CONTACT, OFFICES } from "@/lib/mockData";

const EASE = [0.22, 1, 0.36, 1];

const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-brand-blue text-white">
      <div className="px-5 md:px-10 lg:px-16 pt-16 md:pt-24 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12 pb-14 border-b border-white/15"
        >
          {/* Brand column */}
          <div className="lg:col-span-5">
            <div className="font-display font-extrabold text-2xl md:text-3xl leading-tight">
              SNV ELITE<br />SOLUTIONS
            </div>
            <p className="mt-5 text-white/75 max-w-md leading-relaxed">
              Recruitment, staffing and RPO — trusted across the United States,
              Europe and Canada for 15+ years.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={`mailto:${CONTACT.email}`}
                className="inline-flex items-center gap-2 whitespace-nowrap text-white hover:text-white/85 font-display font-semibold link-underline w-fit"
              >
                <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span>{CONTACT.email}</span>
              </a>
              <a
                href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                className="inline-flex items-center gap-2 whitespace-nowrap text-white hover:text-white/85 font-display font-semibold link-underline w-fit"
              >
                <Phone className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
                <span>{CONTACT.phone}</span>
              </a>
            </div>
          </div>

          {/* Offices */}
          <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {OFFICES.map((o) => (
              <div key={o.country}>
                <div className="text-xs uppercase tracking-[0.18em] font-display font-semibold text-white/60 mb-3">
                  {o.country.includes("India") ? "India · HQ" : "United States"}
                </div>
                <div className="flex items-start gap-2 text-white/85">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" strokeWidth={2} />
                  <span>{o.address}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Explore */}
          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.18em] font-display font-semibold text-white/60 mb-4">
              Explore
            </div>
            <ul className="flex flex-col gap-3">
              <li>
                <Link
                  to="/"
                  data-testid={HOME.footerLinkHome}
                  className="link-underline hover:text-white/85"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  data-testid={HOME.footerLinkTerms}
                  className="link-underline hover:text-white/85 inline-flex items-center gap-1 whitespace-nowrap"
                >
                  Terms &amp; Conditions
                  <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" />
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  data-testid={HOME.footerLinkPrivacy}
                  className="link-underline hover:text-white/85 inline-flex items-center gap-1 whitespace-nowrap"
                >
                  Privacy Policy
                  <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" />
                </Link>
              </li>
            </ul>
          </div>
        </motion.div>

        <div className="mt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-sm text-white/70">
          <div>© {year} SNV Elite Solutions. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
